import {
  ASSIGN_TO_ADDON,
  FIND_ADDON_ASSIGNMENTS,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
} from '@gripp/shared-logic';
import {
  SaveFormTop,
  TopModalBar,
  useGetDimensions,
  LoadingIndicator,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import '../../shared/style/reactTabStyle.css';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { AddonProductAssignmentList } from './addonProductAssignmentList';
import { AddonMediaAssignmentList } from './addonMediaAssignmentList';
import { AddonAssignmentSearchInput } from './addonAssignmentSearchInput';

interface Props {
  addonId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'product' | 'media';
}

export const AddonAssignmentForm = ({
  addonId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const listMaxHeight = height - 50;
  const currentWorkspace = useCurrentWorkspace();

  const [tabIndex, setTabIndex] = useState(initialTab === 'product' ? 1 : 0);

  useEffect(() => {
    if (initialTab) setTabIndex(initialTab === 'product' ? 1 : 0);
  }, [initialTab, addonId]);

  const [productAssignments, setProductAssignments] = useState<string[]>([]);
  const [originalProductAssignments, setOriginalProductAssignments] = useState<
    string[]
  >([]);

  const [mediaAssignments, setMediaAssignments] = useState<string[]>([]);
  const [originalMediaAssignments, setOriginalMediaAssignments] = useState<
    string[]
  >([]);

  const [keyword, setKeyword] = useState('');

  const { data: assignmentsData, isPending: isAssignmentsLoading } =
    useCustomQuery({
      query: FIND_ADDON_ASSIGNMENTS,
      cacheKey: ['addon', 'assignments', addonId],
      variables: { addonId },
    });

  useEffect(() => {
    const products =
      assignmentsData?.addon?.findAddonAssignments?.products || [];
    const media = assignmentsData?.addon?.findAddonAssignments?.media || [];

    setOriginalProductAssignments(products);
    setProductAssignments(products);
    setOriginalMediaAssignments(media);
    setMediaAssignments(media);
  }, [addonId, assignmentsData]);

  const { mutateAsync: assignToAddon, isPending: isAssignLoading } =
    useCustomMutation({ query: ASSIGN_TO_ADDON });

  const handleSubmit = async () => {
    if (!currentWorkspace?.id) return;
    if (tabIndex === 0) {
      // media tab
      await assignToAddon({
        input: {
          addonId,
          workspaceId: currentWorkspace.id,
          mediaIds: mediaAssignments,
        },
      } as any);
    } else {
      await assignToAddon({
        input: {
          addonId,
          workspaceId: currentWorkspace.id,
          productIds: productAssignments,
        },
      } as any);
    }
    queryClient.invalidateQueries({ queryKey: ['addon'], exact: false });
    onComplete();
  };

  const isProductsDirty = () =>
    originalProductAssignments.length !== productAssignments.length ||
    originalProductAssignments.some((p) => !productAssignments.includes(p)) ||
    productAssignments.some((p) => !originalProductAssignments.includes(p));
  const isMediaDirty = () =>
    originalMediaAssignments.length !== mediaAssignments.length ||
    originalMediaAssignments.some((m) => !mediaAssignments.includes(m)) ||
    mediaAssignments.some((m) => !originalMediaAssignments.includes(m));
  const isDirty = tabIndex === 0 ? isMediaDirty() : isProductsDirty();

  const showLoading = isAssignmentsLoading || isAssignLoading; // single mutation pattern mirrors group form logic

  useEffect(() => {
    setKeyword('');
  }, [tabIndex]);

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={!isAssignLoading && isDirty}
    >
      {showLoading ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <AddonAssignmentSearchInput
            keyword={keyword}
            onChange={setKeyword}
            placeholder={tabIndex === 0 ? 'mediaSearchPlaceholder' : 'productSearchPlaceholder'}
          />
          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
              <TabList>
                <Tab>{t('addOn.list.media')}</Tab>
                <Tab>{t('addOn.list.product')}</Tab>
              </TabList>
              <View style={styles.listContainer}>
                <TabPanel>
                  {currentWorkspace?.id && (
                    <AddonMediaAssignmentList
                      workspaceId={currentWorkspace.id}
                      addonId={addonId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      mediaAssignments={mediaAssignments}
                      onMediaAssignmentsChange={setMediaAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {currentWorkspace?.id && (
                    <AddonProductAssignmentList
                      workspaceId={currentWorkspace.id}
                      addonId={addonId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={productAssignments}
                      updateAssignments={setProductAssignments}
                    />
                  )}
                </TabPanel>
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: { flex: 1, marginTop: 20 },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
});
